import React from 'react'

import ApiTrigger from 'components/common/ApiTrigger'
import Button from 'components/common/Button'
import Hr from 'components/common/Hr'
import Input from 'components/common/Input'
import Text from 'components/common/Text'
import TextArea from 'components/common/TextArea'
import Modal, {
  ModalCloseButton,
  ModalTitle,
  ModalHeader,
  ModalSubtitle
} from 'components/Modal'
import theme from 'theme'

const CreateApiKeyConfirmation: React.FC<{
  setOpenState: React.Dispatch<React.SetStateAction<boolean>>
  identifier: string
  description: string
  generatedApiKey: string
}> = ({ setOpenState, identifier, description, generatedApiKey }) => {
  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        justifyItems: 'start',
        alignItems: 'center',
        gap: theme.space[3]
      }}
    >
      <Text as="h4" css={{ paddingLeft: theme.space[2] }}>
        Name
      </Text>
      <Text as="p" css={{ fontSize: 15 }}>
        {identifier}
      </Text>
      <Hr css={{ gridColumn: '1 / span 2' }} />
      {description && (
        <React.Fragment>
          <Text as="h4" css={{ paddingLeft: theme.space[2] }}>
            Description
          </Text>
          <Text as="p" css={{ fontSize: 15 }}>
            {description}
          </Text>
          <Hr css={{ gridColumn: '1 / span 2' }} />
        </React.Fragment>
      )}
      <Text as="h4" css={{ paddingLeft: theme.space[2] }}>
        API Key
      </Text>
      <Text
        as="p"
        css={{ fontSize: 15, wordBreak: 'break-all' }}
        data-test-api-key-confirmation={generatedApiKey}
      >
        {generatedApiKey}
      </Text>
      <Hr
        css={{
          marginBottom: theme.space[3],
          gridColumn: '1 / span 2'
        }}
      />
      <Button
        data-test-create-api-key-close-modal
        primary
        stretch
        css={{
          gridColumn: '1 / span 2'
        }}
        label="Done"
        onClick={() => setOpenState(false)}
      />
    </div>
  )
}

export const CreateApiKeyModal: React.FC<{
  isOpen: boolean
  setOpenState: React.Dispatch<React.SetStateAction<boolean>>
  fetchApiKeys: () => void
}> = ({ isOpen, setOpenState, fetchApiKeys }) => {
  const [{ identifier, description }, updateFormState] = React.useState({
    identifier: '',
    description: ''
  })
  const [generatedApiKey, updateGeneratedKey] = React.useState('')

  // clear the form when closed
  React.useEffect(() => {
    if (!isOpen) {
      updateFormState({
        identifier: '',
        description: ''
      })
      updateGeneratedKey('')
    }
  }, [isOpen])

  function onChange({
    currentTarget: { name, value }
  }:
    | React.FormEvent<HTMLInputElement>
    | React.ChangeEvent<HTMLTextAreaElement>) {
    updateFormState((prev) => ({ ...prev, [name]: value }))
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={() => setOpenState(false)}>
      <ModalCloseButton
        data-test-close-create-api-key-modal
        onClick={() => setOpenState(false)}
      />
      <ModalHeader>
        <ModalTitle>New API Key</ModalTitle>
        <ModalSubtitle>
          <Text as="small" color="normal">
            API Keys will utilize the same permissions as your user role(s).
          </Text>
        </ModalSubtitle>
      </ModalHeader>
      {generatedApiKey ? (
        <CreateApiKeyConfirmation
          description={description}
          generatedApiKey={generatedApiKey}
          identifier={identifier}
          setOpenState={setOpenState}
        />
      ) : (
        <section
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            justifyItems: 'start',
            alignItems: 'center',
            gap: theme.space[2]
          }}
        >
          <label htmlFor="identifier">
            <Text as="h4" css={{ paddingLeft: theme.space[2] }}>
              Name
            </Text>
          </label>
          <Input
            required
            data-test-create-api-key-input="identifier"
            maxLength={100}
            name="identifier"
            placeholder="Name"
            value={identifier}
            onChange={onChange}
          />

          <Text as="small" css={{ gridColumn: '2 / 2' }}>
            Enter a unique name to organize keys.
          </Text>
          <Hr css={{ gridColumn: '1 / span 2' }} />
          <label htmlFor="description">
            <Text as="h4" css={{ paddingLeft: theme.space[2] }}>
              Description
            </Text>
          </label>
          <TextArea
            data-test-create-api-key-input="description"
            maxLength={256}
            name="description"
            placeholder="optional"
            value={description}
            onChange={onChange}
          />
          <Hr
            css={{
              marginBottom: theme.space[3],
              gridColumn: '1 / span 2'
            }}
          />
          <ApiTrigger
            data-test-create-api-key-submit
            primary
            stretch
            as={Button}
            css={{ gridColumn: '1 / span 2' }}
            errorToastMessage="There was an issue creating an API key. Please try again later."
            request={{
              method: 'POST',
              url: '/apikeys',
              data: description
                ? {
                    identifier,
                    description
                  }
                : { identifier }
            }}
            successToastMessage="API key created."
            onSubmit={(e) => e.preventDefault()}
            onSuccess={({ response }) => {
              updateGeneratedKey(response.data)
              fetchApiKeys()
            }}
          >
            Save
          </ApiTrigger>
        </section>
      )}
    </Modal>
  )
}
